import { solutionHeaderConfigs } from "./solutions";

export const siteConfig = {
  name: "Buddify",
  tagline: "AI-Driven Commerce, Built Around Your Store",
  description:
    "Buddify is the platform for AI-driven ecommerce. Help shoppers buy with Buddy AI, grow through brand partnerships, and reach new customers.",
  url: "https://buddify.app",
  ogImage: "https://buddify.app/opengraph-image",
  links: {
    shopifyApp: "https://apps.shopify.com/buddify",
    aiApp: "https://apps.shopify.com/buddify-ai-discount-negotiator",
    support: "https://support.buddify.app",
    email: "support@buddify.app",
  },
  social: {
    twitter: "@buddifyapp",
    x: "https://x.com/BuddifyB2B",
    youtube: "https://www.youtube.com/@buddifyapp",
    facebook: "https://www.facebook.com/people/Buddify-App/100089657117965/",
    instagram: "https://www.instagram.com/buddifyapp/",
    github: "https://github.com/BuddifyApp",
  },
};

export const navigation = {
  products: [
    {
      name: "Brand Partnerships",
      description: "Partner dropshipping, wholesale & marketplace",
      href: "/brand-partnerships",
      icon: "Handshake",
    },
    {
      name: "Buddy AI Assistant",
      description: "AI Sales, Support & Conversion team",
      href: "/buddy-ai-assistant",
      icon: "Bot",
      badge: "Built for Shopify",
    },
    {
      name: "Marketplace",
      description: "Sell directly to customers",
      href: "/marketplace",
      icon: "Store",
    },
  ],
  main: [
    { name: "Partners", href: "/partners" },
    { name: "Pricing", href: "/pricing" },
    { name: "Blog", href: "/blog" },
    { name: "Community", href: "/community" },
    { name: "Contact", href: "/contact" },
  ],
  legal: [
    { name: "Legal Center", href: "/legal" },
    { name: "Privacy Policy", href: "/brand-partnerships/privacy" },
    { name: "Terms of Use", href: "/brand-partnerships/terms" },
    { name: "Cookie Policy", href: "/cookie" },
  ],
};

export const stats = {
  // Average rating across both apps: (5.0 + 4.7) / 2 = 4.85 ≈ 4.9
  rating: 4.8,
  ratingLabel: "avg across 2 apps",
  reviews: 75,
  brands: "6,000+",
  partnerships: "30,000+",
  countries: "25+",
};

// Try Now Free button products (used in header)
export const tryNowProducts = [
  {
    name: "Brand Partnerships",
    tagline: "Dropshipping & Wholesale",
    href: "https://apps.shopify.com/buddify",
    icon: "Handshake",
    color: "primary",
  },
  {
    name: "Buddy AI Assistant",
    tagline: "AI Sales, Support & Conversion",
    href: "https://apps.shopify.com/buddify-ai-discount-negotiator",
    icon: "Bot",
    color: "secondary",
  },
  {
    name: "Marketplace",
    tagline: "Sell to Customers",
    icon: "Store",
    color: "tertiary",
    links: [
      {
        label: "List Products",
        sublabel: "Shopify App",
        href: "https://apps.shopify.com/buddify",
      },
      {
        label: "Shop Deals",
        sublabel: "iOS App",
        href: "https://apps.apple.com/app/id6478976454",
      },
    ],
  },
] as const;

// Single source of truth for all product-specific stats
export const productStats = {
  brandPartnerships: {
    name: "Brand Partnerships",
    appStoreUrl: "https://apps.shopify.com/buddify?utm_source=buddify.app",
    brands: "6,000+",
    partnerships: "30,000+",
    countries: "25+",
    salesIncrease: "29%",
    aovIncrease: "26%",
    conversionIncrease: "30%",
    rating: 4.7,
    reviews: 85,
  },
  aiAssistant: {
    name: "Buddy AI Assistant",
    appStoreUrl:
      "https://apps.shopify.com/buddify-ai-discount-negotiator?utm_source=buddify.app",
    conversionIncrease: "24%",
    timeOnStore: "55%",
    aovIncrease: "16%",
    abandonmentReduction: "18%",
    returningCustomers: "13%",
    rating: 4.9,
    reviews: 45,
  },
  marketplace: {
    name: "Marketplace",
    products: "200,000+",
    discountGuarantee: "25%",
    maxDiscount: "80%",
    categories: "10+",
  },
};

// Apple-style two-level header config for product and solution pages.
// Community subpages render their data-aware subnav locally.
export const subNavHeaderConfig = [
  {
    id: "brand-partnerships",
    name: "Brand Partnerships",
    href: "/brand-partnerships",
    theme: "light" as const,
    links: [
      { name: "Overview", href: "/brand-partnerships" },
      { name: "Pricing", href: "/brand-partnerships/pricing" },
    ],
    cta: {
      label: "Install for Free",
      href: "https://apps.shopify.com/buddify?utm_source=buddify.app",
      external: true,
    },
  },
  {
    id: "buddy-ai",
    name: "Buddy AI",
    href: "/buddy-ai-assistant",
    theme: "dark" as const,
    links: [
      { name: "Overview", href: "/buddy-ai-assistant" },
      { name: "Pricing", href: "/buddy-ai-assistant/pricing" },
    ],
    cta: {
      label: "Install for Free",
      href:
        "https://apps.shopify.com/buddify-ai-discount-negotiator?utm_source=buddify.app",
      external: true,
    },
  },
  {
    id: "marketplace",
    name: "Marketplace",
    href: "/marketplace",
    theme: "blue" as const,
    links: [{ name: "Overview", href: "/marketplace" }],
    cta: {
      label: "Get on iOS App Store",
      href: "https://apps.apple.com/app/id6478976454",
      external: true,
    },
  },
  {
    id: "dashboard",
    name: "Dashboard",
    href: "/dashboard",
    backHref: "/community",
    theme: "light" as const,
    links: [
      { name: "Overview", href: "/dashboard" },
      { name: "Products", href: "/dashboard/products" },
      { name: "Orders", href: "/dashboard/orders" },
      { name: "Partners", href: "/dashboard/partners" },
    ],
  },
  ...solutionHeaderConfigs,
] as const;

/** @deprecated Use `subNavHeaderConfig` instead. */
export const productHeaderConfig = subNavHeaderConfig.filter(
  (
    c
  ): c is
    | typeof subNavHeaderConfig[0]
    | typeof subNavHeaderConfig[1]
    | typeof subNavHeaderConfig[2] =>
    c.id === "brand-partnerships" ||
    c.id === "buddy-ai" ||
    c.id === "marketplace",
);
